<script lang="ts" setup>
import {
  AccountRouteEnum,
  AuthTabEnum,
  DepositRouteEnum,
  RoutesEnum,
} from '~/enums'
import { useRoute } from 'vue-router'
import { useAuthStore } from '~/stores/auth'
import { PROFILE_MENUS, HEADER_MENUS } from '~/resources/menus'
import { useUserOnboardingEvents } from '~/composables/tracking-events/use-user-onboarding-events'
import { useMeta } from '~/composables/common/use-meta'
import { useModalsStore } from '#lib/stores'

defineProps({
  isShowSidebar: {
    type: Boolean,
    default: false,
  },
})

const { openAuthModal } = useAuth()
const modalStore = useModalsStore()
const { isOpenAuthentication } = storeToRefs(modalStore)
const setOpenAuthModal = modalStore.setOpenAuthModal
const route = useRoute()
const { isLogged, user, formattedBalance } = useUser()
const authStore = useAuthStore()
const { signUpInitiated } = useUserOnboardingEvents()
const { currentMeta } = useMeta()

onMounted(() => {
  if (route.query?.token && route.path === AccountRouteEnum.ResetPassword) {
    authStore.setForgotPasswordStepReset(true)
    authStore.setSelectedAuthTab(AuthTabEnum.ResetPassword)
  }
})

const isOpenAuthenticationInterceptor = computed({
  get: () => !isLogged.value && isOpenAuthentication.value,
  set: (value) => {
    if (!value && authStore.enablePreEscape) {
      authStore.setWarningEscaped(true)
      return
    }
    setOpenAuthModal(value)
  },
})

const isShowWelcomeModal = computed(() => {
  return authStore.enableWelcomeModal || false
})

defineEmits(['toggleSidebar'])

const openModalWithLogin = () => {
  authStore.setSelectedAuthTab(AuthTabEnum.Login)
  openAuthModal()
}

const openModalWithRegister = () => {
  signUpInitiated()
  authStore.setSelectedAuthTab(AuthTabEnum.Register)
  openAuthModal()
}

const onAuthTabChange = (tab: AuthTabEnum) => {
  authStore.setSelectedAuthTab(tab)
}
</script>
<template>
  <header id="header" class="header desktop-header">
    <div class="header__block-left">
      <div
        v-if="isShowSidebar"
        class="icon-slider-hamburger"
        button-id="toggle-sidebar"
        button-name="toggle-sidebar"
        @click="$emit('toggleSidebar')"
      >
        <img src="/images/header/icon-hamburger-pc.svg" alt="icon hamburger" width="24" height="24" />
      </div>
      <h1 class="header__logo" :title="currentMeta.heading_1">
        <NuxtLink :to="RoutesEnum.Home">
          <img
            src="/images/logo/logo.svg"
            alt="Header Logo"
            width="130"
            height="48"
          />
        </NuxtLink>
      </h1>
    </div>
    <div class="layout__container">
      <div v-if="isShowSidebar" class="layout__sidebar" />
      <div class="layout__content">
        <div class="content-page">
          <div
            class="header__menus container"
            :class="{ 'header__menus--none-sidebar': !isShowSidebar }"
          >
            <BaseHeaderTabs v-model="route.fullPath" :tabs="HEADER_MENUS" />
            <div class="header__block-right">
              <template v-if="!isLogged">
                <BaseButton
                  label="Đăng nhập"
                  variant="outline"
                  size="large"
                  button-id="header-login"
                  button-name="header-login"
                  @click="openModalWithLogin"
                />
                <BaseButton
                  label="Đăng ký"
                  variant="primary"
                  size="large"
                  button-id="header-register"
                  button-name="header-register"
                  @click="openModalWithRegister"
                />
              </template>
              <div v-if="isLogged" class="flex-middle-space-end">
                <div class="header-logged">
                  <div class="header-logged__wrapper">
                    <span class="icon-wallet" />
                    <div class="line" />
                    <ClientOnly>
                      <div class="header-logged__balance line-limit-1">
                        {{ formattedBalance }}
                      </div>
                    </ClientOnly>
                  </div>
                  <NuxtLink :to="DepositRouteEnum.QRPay" class="deposit-button">
                    <BaseButton
                      label="Nạp tiền"
                      prefix-icon="icon-plus-circle"
                      size="large"
                      variant="primary"
                      button-id="header-deposit"
                      button-name="header-deposit"
                    />
                  </NuxtLink>
                </div>
                <BaseMenu
                  :items="PROFILE_MENUS"
                  open-on-hover
                  custom-class="profile-menu desktop"
                >
                  <NuxtLink :to="AccountRouteEnum.Account" class="account-info">
                    <div class="account-info__wrapper">
                      <div class="account-info__text-welcome">Welcome!</div>
                      <ClientOnly>
                        <div class="account-info__username line-limit-1">
                          {{ user.fullname }}
                        </div>
                      </ClientOnly>
                    </div>
                    <div class="account-info__avatar">
                      <img
                        class="account-info__avatar-img"
                        src="/assets/images/avatar/avatar-1.webp"
                        alt="avatar 1"
                      />
                    </div>
                  </NuxtLink>
                </BaseMenu>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <ModalAuthentication
      v-model="isOpenAuthenticationInterceptor"
      :selected-tab="authStore.selectedAuthTab"
      :on-change-tab="onAuthTabChange"
    />
    <ModalForgotPasswordSendMail />
    <ModalForgotPasswordSendReset />
    <ModalWelcome :is-show-welcome-modal="isShowWelcomeModal" />
  </header>
</template>
<style lang="scss" scoped>
@import '~/assets/styles/components/desktop/header/index.scss';
@import '~/assets/styles/components/responsive/desktop/header/index.scss';
</style>
